import {
  ErrorMessage,
  GroupId,
  InlineMessage,
  Message,
  SuccessMessage,
} from '@sr/types';
import { getActiveMessage } from './message';
import type { Store } from 'store';
import useStore from 'store';
import { stringArraysEqual } from 'store/equality-functions';

export function getMessages(state: Store): ErrorMessage[] {
  const messages: ErrorMessage[] = [];
  state.messages.forEach((message, id) => {
    if (message.displayType === 'block') {
      messages.push(message);
    }
    if (message.displayType === 'inline') {
      messages.push(message);
    }
  });
  return sortMessages(messages);
}

export function useAllMessages() {
  return useStore(getMessages);
}

export function getMessagesForActiveGroup(state: Store): ErrorMessage[] {
  const { activeGroupId } = state;
  if (activeGroupId) {
    return getMessagesForGroup(activeGroupId)(state);
  } else {
    return [];
  }
}

export function getMessagesForGroup(groupId: GroupId) {
  return function (state: Store): ErrorMessage[] {
    const messages = getMessages(state).filter(
      (message) => message.ruleInfo.groupId === groupId
    );
    // Put the free messages first for free users
    if (!state.isPro) {
      return messages.sort((a, b) => {
        if (
          a.ruleInfo.showToFreeUsers === true &&
          !b.ruleInfo.showToFreeUsers
        ) {
          return -1;
        }
        return 0;
      });
    } else {
      return messages;
    }
  };
}

export function useMessagesForGroup(groupId: GroupId) {
  return useStore(getMessagesForGroup(groupId));
}

export function getMessageIdsForGroup(groupId: GroupId) {
  return function (state: Store): string[] {
    return getMessagesForGroup(groupId)(state).map((msg) => msg.id);
  };
}

export function useMessagesIdsForGroup(groupId: GroupId) {
  return useStore(getMessageIdsForGroup(groupId), stringArraysEqual);
}

export function getMessageCountForGroup(groupId: GroupId) {
  return function (state: Store): number {
    return getMessagesForGroup(groupId)(state).length;
  };
}

export function useMessageCountForGroup(groupId: GroupId) {
  return useStore(getMessageCountForGroup(groupId));
}

export function getMessagesToUnderline(srId: string) {
  return function (state: Store): InlineMessage[] {
    const message = getActiveMessage(state);
    if (message && message.srId === srId && message.displayType === 'inline') {
      return [message];
    }
    return [];
  };
}

export function getMessageIdsToUnderline(srId: string) {
  return function (state: Store): string[] {
    return getMessagesToUnderline(srId)(state).map((msg) => msg.id);
  };
}

export function useMessageIdsToUnderline(srId: string) {
  return useStore(getMessageIdsToUnderline(srId), stringArraysEqual);
}

// Remove all but one message for each ruleId. This is used to prevent showing
// multiple success messages (we have one for each field from the server).
function deduplicateMessages(messages: SuccessMessage[]): SuccessMessage[] {
  return messages.filter((message, index, messages) => {
    return (
      index ===
      messages.findIndex((msg) => {
        return message.ruleInfo.title === msg.ruleInfo.title;
      })
    );
  });
}

// Sort messages from error to ok status
function sortMessages<T extends Message>(messages: T[]): T[] {
  const order = { error: 3, warning: 2, info: 1, ok: 0 };
  return messages.sort((a, b) => order[b.status] - order[a.status]);
}
